import {
    FeatureType,
    FolderPermissionLevel,
    FolderRoleType,
    GetFolderResponse,
    RoleAssignment
} from '../types';
import { LitElement, css, html } from 'lit';
import { addRoleForEntity, createRoleRequestFromRole, removeRoleForEntity } from '../helpers/api/roles-api';
import { customElement, property } from 'lit/decorators';

import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import featuresInfo from '../helpers/state/features-info';
import { undefinedIfEmpty } from '../helpers/utils';
import { validateEmail } from '../helpers/strings';
import { getMainPage } from '../helpers/page-name';

@customElement('manage-folder-access-component')
export class ManageFolderAccessComponent extends LitElement {
    static amplitude_callsite: string = 'Manage Folder Access Modal';

    @property({ type: Object })
    folder?: GetFolderResponse;

    @property({ type: String })
    folder_id?: string;

    @property({ type: Object })
    roles?: RoleAssignment[];

    private readonly bindAccountState = new StateController(this, accountInfo);

    private async handleSubmit(e: any) {
        const email = e.detail.value.toLowerCase().trim();
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            if (
                this.folder === undefined ||
                this.roles === undefined ||
                this.folder_id === undefined ||
                email === undefined
            )
                return;
            if (email === '' || this.roles.map((r) => r.email).includes(email)) {
                getMainPage().errorToastElement.showToast(
                    'Email cannot be an empty string or a duplicate of an existing email'
                );
                return;
            }
            if (!validateEmail(email)) {
                getMainPage().errorToastElement.showToast('Invalid email format.');
                return;
            }
            if (!undefinedIfEmpty(e.detail.option)) {
                getMainPage().errorToastElement.showToast('Select access type.');
                return;
            }
            const new_access_level = undefinedIfEmpty(e.detail.option);
            if (new_access_level === 'Owner') {
                await addRoleForEntity(
                    ManageFolderAccessComponent.amplitude_callsite,
                    this.folder_id,
                    {
                        email,
                        level: { FolderPermissionLevelType: FolderPermissionLevel.OWNER },
                    }
                );
            } else if (new_access_level === 'Editor') {
                await addRoleForEntity(
                    ManageFolderAccessComponent.amplitude_callsite,
                    this.folder_id,
                    {
                        email,
                        level: { FolderPermissionLevelType: FolderPermissionLevel.WRITER },
                    }
                );
            } else if (new_access_level === 'Collaborator') {
                await addRoleForEntity(
                    ManageFolderAccessComponent.amplitude_callsite,
                    this.folder_id,
                    {
                        email,
                        level: {
                            FolderPermissionLevelType: FolderPermissionLevel.COLLABORATOR,
                        },
                    }
                );
            }

            getMainPage().errorToastElement.showToast('New access added');
            await this.dispatchEvent(new CustomEvent('updated', {}));
        }, 'An error has occurred.  Context: could not update the folder.');
        await this.requestUpdate();
    }

    private async removeAdmin(e: CustomEvent) {
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            if (
                this.folder === undefined ||
                this.roles === undefined ||
                this.folder_id === undefined
            )
                return;
            const role_to_delete = this.roles.filter((w) => w.email === e.detail.email);
            await removeRoleForEntity(
                ManageFolderAccessComponent.amplitude_callsite,
                this.folder_id,
                createRoleRequestFromRole(role_to_delete[0])
            );
            await this.dispatchEvent(new CustomEvent('updated', {}));
        }, 'An error has occurred.  Context: could not update the account.');
        await this.requestUpdate();
    }

    render() {
        return html`
            ${featuresInfo.addFolderEditorsEnabled ? html`
                <div class="grantAccessContent">
                    <h2 id="grantAccessTitle">
                        <mwc-icon>folder_shared</mwc-icon>
                        Manage Folder Access
                    </h2>
                    <div style="margin-top: 2rem">
                        <form-input-access
                            type="folder"
                            placeholder="Add by email address"
                            @submit="${this.handleSubmit}"
                            ?isOwner=${this.folder?.is_owner}
                        >
                            Send Invite
                        </form-input-access>
                    </div>
                    <div class="innerBox">
                        ${this.roles
                            ?.filter(
                                (r) =>
                                    (r.role as FolderRoleType).FolderRoleType?.level ===
                                    FolderPermissionLevel.OWNER
                            )
                            .map(
                                (c) => html`
                                    <access-item
                                        email="${c.email}"
                                        @removed="${this.removeAdmin}"
                                        ?canDelete=${this.folder?.is_owner}
                                        accessLevel="Owner"
                                    >
                                    </access-item>
                                `
                            )}
                        ${this.roles
                            ?.filter(
                                (r) =>
                                    (r.role as FolderRoleType).FolderRoleType?.level ===
                                    FolderPermissionLevel.WRITER
                            )
                            .map(
                                (c) => html`
                                    <access-item
                                        email="${c.email}"
                                        @removed="${this.removeAdmin}"
                                        ?canDelete=${this.folder?.is_writer}
                                        accessLevel="Editor"
                                    >
                                    </access-item>
                                `
                            )}
                        ${this.roles
                            ?.filter(
                                (r) =>
                                    (r.role as FolderRoleType).FolderRoleType?.level ===
                                    FolderPermissionLevel.COLLABORATOR
                            )
                            .map(
                                (c) => html`
                                    <access-item
                                        email="${c.email}"
                                        @removed="${this.removeAdmin}"
                                        canDelete
                                        accessLevel="Collaborator"
                                    >
                                    </access-item>
                                `
                            )}
                    </div>
                    <error-toast id="errorToast"></error-toast>
                    <div></div>
                </div>
            ` : html`<upgrade-dialog .featureType="${FeatureType.AddFolderEditors}"></upgrade-dialog>`
            }
        `;
    }

    static get styles() {
        return css`
            .grantAccessContent {
                width: 550px;
                padding: 0.5rem 1.5rem 1rem;
            }

            #grantAccessTitle {
                margin-bottom: 1.25rem;
                color: var(--main-primary-color);
                display: flex;
                align-items: center;
                justify-content: center;
            }

            mwc-icon {
                width: 2rem;
            }

            #emailAddress {
                width: 550px;
                max-width: 100%;
                display: block;
            }

            .innerBox {
                border-radius: 0.75rem;
                border: 1px solid var(--theme-border-color);
                padding: 1rem;
                margin-top: 1.25rem;
                max-height: 60%;
                overflow-x: hidden;
                overflow-y: auto;
            }

            access-item {
                display: block;
                border-bottom: 1px solid var(--theme-border-color);
                padding-bottom: 1rem;
                margin-bottom: 1rem;
            }

            access-item:last-of-type {
                border-bottom: none;
                margin-bottom: 0;
                padding-bottom: 0;
            }

            @media (max-width: 900px) {
                .grantAccessContent {
                    width: calc(100% - 2rem);
                    padding: 0.5rem 1rem 1rem;
                }
            }
        `;
    }
}
